import { useSelector } from "react-redux";
import { SelectorState } from "../Selectors/index.selector";
import { AbilityBuilder, MongoQuery, AbilityClass, PureAbility, MongoAbility} from '@casl/ability';
import { objects } from "../Constants/Admins.constants";

type AppAbility = MongoAbility<[Actions, Subjects], MongoQuery>

export enum Actions {
    Manage = 'manage',
    Read = 'read',
    Create = 'create',
    Update = 'update',
    Delete = 'delete',
    Self = 'Self',
}
type Subjects = objects;

const offerObjects = ["CartOffer", "PromoCode", "PaymentMethodDiscount"];
const settingObjects = ["Admin", "Driver", "Team", "SocialLink", "PaymentMethod"];
const orderObjects = ["ScrapingOrder"];

// Synthetic subject gating the App Settings drawer entry. A user gets Read on
// AppSetting if they have ANY permission for a child of that page; the page
// then shows a tile for each child they can read.
// DashboardSetting is granted unconditionally to permissioned users so the
// Security tab (gated by Self,Admin) is always reachable.
const appSettingObjects = [
    "Layout", "Product", "ScrapingItem", "Advertisement",
    "MainSection", "Category", "Group", "Outfit",
    "PaymentMethod", "SocialLink", "Team", "TryOnAi",
    "OrderStateGroup", "ScreenMessage", "ShippingRate",
];

// Synthetic subject for the Warehouse scan page (Sorting + Pack-by-Order live on
// one page with a mode toggle). Read is granted if the user can do EITHER flow,
// so the drawer entry + route are reachable; each mode stays gated by its own
// real subject (Sorting / OrderPacking) inside the page.
const warehouseScanObjects = ["Sorting", "OrderPacking"];

const repackagingObjects = ["WarehouseArrival", "Carton", "Manifest", "Bin", "WarehouseException"];

// Synthetic subject gating the Business drawer entry, the same way AppSetting
// gates Akkooo App: Read is granted when the user holds ANY child of the
// Business page, and the page shows a tile for each one they can read.
const businessSettingObjects = ["ScrapingItem"];

// Synthetic subjects for the single sidebar entries that open a tile page
// (Pages/Management, Pages/TimeLeave, Pages/HumanResources), same rule as
// AppSetting. Keep each list in step with that page's tiles.
const managementObjects = ["ERP", "PIM", "CMS", "OMS"];
const timeLeaveObjects = ["TimeSheets", "Attendacne", "TimeOff"];
const humanResourcesObjects = ["Employees", "Recruitement"];

// The backend's own super-admin type, checked verbatim. It is not the same as
// `type: "Admin"` — that is the everyday admin (which `buildAbility` still
// grants `manage all` here), and the server refuses it for super-admin-only
// mutations such as the order-status arrangement.
export const isSuperAdmin = (info: any) => info?.type === "Super Admin";


// Pure builder so the same ability can be computed inside hooks (CaslProvider)
// AND outside React (e.g. in a redux action right after login dispatches the
// auth payload, before any component has had a chance to re-render).
//
// Loose `info` shape — buildAbility only reads `type`, `email`, `permissions`.
// Accepting `any` lets callers pass the redux info OR the auth-context info
// (different fields) without casting at every call site.
export const buildAbility = (info: any) => {
    let canOffer = false;
    let canSetting = false;
    let canOrder = false;
    let canAppSetting = false;
    let canWarehouseScan = false;
    let canRepackaging = false;
    let canBusinessSetting = false;
    let canManagement = false;
    let canTimeLeave = false;
    let canHumanResources = false;

    const { can, cannot, build} = new AbilityBuilder(PureAbility as  AbilityClass<AppAbility>);
    if(info?.type === "Admin") {
        can(Actions.Manage, 'all');
        cannot(Actions.Read, "DriverTransactions");
    } else if(info?.permissions){
        can(Actions.Self, "Admin");
        // Always grant DashboardSetting so the Security tab (Self,Admin) is
        // reachable from the drawer for every permissioned user.
        can(Actions.Read, "DashboardSetting");
        info?.permissions?.map((item: { object: string; abilities: string[] }) => {
            if(offerObjects.includes(item.object)) canOffer = true;
            if(settingObjects.includes(item.object)) canSetting = true;
            if(orderObjects.includes(item.object)) canOrder = true;
            if(appSettingObjects.includes(item.object)) canAppSetting = true;
            if(warehouseScanObjects.includes(item.object)) canWarehouseScan = true;
            if(repackagingObjects.includes(item.object)) canRepackaging = true;
            if(businessSettingObjects.includes(item.object)) canBusinessSetting = true;
            if(managementObjects.includes(item.object)) canManagement = true;
            if(timeLeaveObjects.includes(item.object)) canTimeLeave = true;
            if(humanResourcesObjects.includes(item.object)) canHumanResources = true;
            return can(item.abilities as any, item.object as any);
        });
        if(canOffer) can(Actions.Read, "Offer");
        if(canSetting) can(Actions.Read, "Setting");
        if(canOrder) can(Actions.Read, "Address");
        if(canAppSetting) can(Actions.Read, "AppSetting");
        if(canWarehouseScan) can(Actions.Read, "WarehouseScan");
        if(canRepackaging) can(Actions.Read, "Repackaging");
        if(canBusinessSetting) can(Actions.Read, "BusinessSetting");
        if(canManagement) can(Actions.Read, "Management");
        if(canTimeLeave) can(Actions.Read, "TimeLeave");
        if(canHumanResources) can(Actions.Read, "HumanResources");

    } else if(!info?.email) {
        can(Actions.Read, "ScrapingOrder");
        can(Actions.Read, "Home");
        can(Actions.Read, "DriverTransactions");
    }
    return build();
};

export type Ability = ReturnType<typeof buildAbility>;

export const CaslProvider = () => {
    const { info } = useSelector((state :SelectorState) => state.Auth);
    return buildAbility(info);
  }

const ownPermission = (info: any, action: Actions, object: Subjects) =>
    info?.type === "Admin" ||
    Boolean(info?.permissions?.some((item: { object: string; abilities: string[] }) =>
        item.object === object && item.abilities?.includes(action)));

export const useOwnPermission = () => {
    const { info } = useSelector((state :SelectorState) => state.Auth);
    return (action: Actions, object: Subjects) => ownPermission(info, action, object);
};